import type {Component, ComponentDefinition, Editor, StyleProps} from 'grapesjs';
import {EMAIL_MAKER_COMPONENT_TYPES, EMAIL_MAKER_PROPS} from '@EmailMakerReact/email-maker-react.constants';
import type {ComponentAlignmentStyle} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {EmailMakerMediaType} from '@EmailMakerReact/email-maker-react.types';
import {colorToHex} from '@Utils/color.util';

const TABLE_TAG_NAME = 'TABLE';
const CELL_TAG_NAME = 'TD';
const ANCHOR_TAG_NAME = 'a';

/** Shared interaction flags for the inner button table parts. */
const BUTTON_TABLE_PART_FLAGS = {
  highlightable: false,
  hoverable: false,
  selectable: false,
  badgable: false,
  droppable: false,
  draggable: false,
  copyable: false,
} as const;

/** The PMW wrapper component of the email. */
export function getMainWrapper(editor: Editor): Component | undefined {
  return editor.DomComponents.getWrapper()?.find(`[data-gjs-type="${EMAIL_MAKER_COMPONENT_TYPES.container.PMW_WRAPPER}"]`)[0];
}

/** Recursive find by type list, starting below `root` (the main wrapper when omitted). Matched components are not descended into. */
export function getAllComponentsByType(editor: Editor, types: string[], root?: Component): Component[] {
  const parent = root ?? getMainWrapper(editor);
  const result: Component[] = [];

  if (!parent) {
    return result;
  }

  for (const child of parent.components().models) {
    const type = child.get('type');
    if (type && types.includes(type)) {
      result.push(child);
    } else {
      result.push(...getAllComponentsByType(editor, types, child));
    }
  }

  return result;
}

/** Resolves the media Component (image/video/sticker/icon/design) rendered by the given canvas element, if any. */
export function getMediaComponentFromElement(editor: Editor, element: HTMLElement): Component | undefined {
  return getAllComponentsByType(editor, Object.values(EMAIL_MAKER_COMPONENT_TYPES.graphic)).find((component) => component.getEl() === element);
}

/** Collects `root` and every descendant, optionally filtered to the given types (the whole tree is always traversed). */
export function getAllComponentsInTree(root: Component, types: string[] = [], result: Component[] = []): Component[] {
  const type = root.get('type') ?? '';
  if (types.length === 0 || (type && types.includes(type))) {
    result.push(root);
  }

  root.components().each((child: Component) => {
    getAllComponentsInTree(child, types, result);
  });

  return result;
}

/** Tag-name check, not component-type check. */
export function isOfTypeTable(editor: Editor, component?: Component): boolean {
  return doesComponentHaveTagName(component ?? editor.getSelected(), TABLE_TAG_NAME);
}

/** Tag-name check, not component-type check. */
export function isOfTypeCell(editor: Editor, component?: Component): boolean {
  return doesComponentHaveTagName(component ?? editor.getSelected(), CELL_TAG_NAME);
}

export function isOfTypeGraphic(editor: Editor, component?: Component): boolean {
  const target = component ?? editor.getSelected();
  if (!target) {
    return false;
  }

  const type = target.get('type');

  return (
    type === EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_VIDEO ||
    type === EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_IMAGE ||
    type === EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_STICKER ||
    type === EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_DESIGN
  );
}

export function isOfTypeContainer(editor: Editor, component?: Component): boolean {
  const target = component ?? editor.getSelected();
  if (!target) {
    return false;
  }

  const type = target.get('type');

  return (
    type === EMAIL_MAKER_COMPONENT_TYPES.container.PMW_CONTENT ||
    type === EMAIL_MAKER_COMPONENT_TYPES.container.PMW_WRAPPER ||
    type === EMAIL_MAKER_COMPONENT_TYPES.container.PMW_WRAPPER_CELL
  );
}

/** Walks up from `child`, stopping at section boundaries. */
export function isComponentInsideComponent(child: Component, parent: Component): boolean {
  let innerComponent: Component | undefined = child;

  while (innerComponent) {
    if (innerComponent === parent) {
      return true;
    }

    if (innerComponent.get('type') === EMAIL_MAKER_COMPONENT_TYPES.table.PMW_SECTION) {
      return false;
    }

    innerComponent = innerComponent.parent();
  }

  return false;
}

export function isComponentWidthUnset(editor: Editor, component?: Component): boolean {
  const target = component ?? editor.getSelected();
  if (!target) {
    return false;
  }

  const {width} = target.getStyle();
  return width === 'unset';
}

/** Whether the component type supports a width style. */
export function hasWidthProperty(component?: Component): boolean {
  if (!component) {
    return false;
  }

  const type = component.get('type');

  return (
    type === EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_IMAGE ||
    type === EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_DESIGN ||
    type === EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_STICKER ||
    type === EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_VIDEO ||
    type === EMAIL_MAKER_COMPONENT_TYPES.social.PMW_SOCIALS_IMAGE ||
    type === EMAIL_MAKER_COMPONENT_TYPES.table.PMW_TABLE ||
    type === EMAIL_MAKER_COMPONENT_TYPES.table.PMW_SECTION ||
    type === EMAIL_MAKER_COMPONENT_TYPES.table.PMW_CELL ||
    type === EMAIL_MAKER_COMPONENT_TYPES.text.PMW_BUTTON
  );
}

export function doesComponentHaveLink(component?: Component): boolean {
  if (!component) {
    return false;
  }

  return (
    component.is(EMAIL_MAKER_COMPONENT_TYPES.social.PMW_SOCIALS_ITEM) ||
    component.is(EMAIL_MAKER_COMPONENT_TYPES.text.PMW_LINK) ||
    component.is(EMAIL_MAKER_COMPONENT_TYPES.text.PMW_BUTTON)
  );
}

/** Finds the component carrying the given drop identifier. */
export function getComponentByLoadIdentifier(editor: Editor, identifier: string): Component | undefined {
  return editor.DomComponents.getWrapper()?.find(`[${EMAIL_MAKER_PROPS.DROP_IDENTIFIER}="${identifier}"]`)[0];
}

/** Reads one style property off the component (or selection), null when unset. */
export function getComponentStyle(editor: Editor, property: string, component?: Component): StyleProps[string] | null {
  const target = component ?? editor.getSelected();
  if (!target) {
    return null;
  }

  const value = target.getStyle()[property];

  if (typeof value === 'undefined') {
    return null;
  }

  return value;
}

/** Whether the component carries a real background-image style. */
export function hasBackgroundImage(component: Component): boolean {
  const backgroundImage = component.getStyle()['background-image'];
  return typeof backgroundImage === 'string' && backgroundImage !== 'none' && backgroundImage !== '';
}

export function getComponentMediaType(editor: Editor, component?: Component): EmailMakerMediaType {
  const target = component ?? editor.getSelected();

  return target?.get('type') === EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_VIDEO ? EmailMakerMediaType.VIDEO : EmailMakerMediaType.IMAGE;
}

/** Whether a media component's media-type prop marks it as a video (vs an image). */
export function isVideoMediaComponent(component: Component): boolean {
  return getStringProp(component, EMAIL_MAKER_PROPS.MEDIA_TYPE) === EmailMakerMediaType.VIDEO;
}

/** Derives alignment from margins, falling back to the parent's align attribute. */
export function getComponentHorizontalAlignment(editor: Editor, component?: Component): ComponentAlignmentStyle | undefined {
  const target = component ?? editor.getSelected();
  if (!target?.getStyle()) {
    return undefined;
  }

  const elementStyles = target.getEl()?.style;
  const alignAttribute: unknown = target.parent()?.getAttributes().align;
  const parentAlignment = typeof alignAttribute === 'string' ? alignAttribute : '';

  if (elementStyles?.marginLeft === 'auto' && elementStyles.marginRight === 'auto') {
    return 'center';
  }
  if (elementStyles?.marginRight === 'auto' && parseInt(elementStyles.marginLeft, 10) === 0) {
    return 'left';
  }
  if (elementStyles?.marginLeft === 'auto' && parseInt(elementStyles.marginRight, 10) === 0) {
    return 'right';
  }

  switch (parentAlignment) {
    case 'center':
    case 'middle':
      return 'center';
    case 'left':
      return 'left';
    case 'right':
      return 'right';
    default:
      return undefined;
  }
}

/** Returns the wrapping anchor when the component is link-wrapped. */
export function getComponentForToggleSwitch(editor: Editor, component?: Component): Component | undefined {
  const target = component ?? editor.getSelected();

  if (!target) {
    return undefined;
  }

  const parent = target.parent();
  if (parent && parent.get('tagName')?.toLowerCase() === ANCHOR_TAG_NAME) {
    return parent;
  }

  return target;
}

/** The inner table definition of a PMW button. This is serialized into saved templates. */
export function getTableComponentForButton(text?: string): ComponentDefinition {
  const buttonText = text?.trim() ? text : window.i18next.t('pmwjs_button_click');

  return {
    type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_TABLE,
    tagName: 'table',
    ...BUTTON_TABLE_PART_FLAGS,
    editable: false,
    style: {
      'text-align': 'inherit',
      width: '100%',
      'font-weight': 'inherit',
      'font-style': 'inherit',
      color: 'inherit',
      'font-size': 'inherit',
      'line-height': 'inherit',
    },
    components: [
      {
        type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_ROW,
        tagName: 'tr',
        ...BUTTON_TABLE_PART_FLAGS,
        editable: false,
        components: [
          {
            type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_CELL,
            tagName: 'td',
            ...BUTTON_TABLE_PART_FLAGS,
            editable: false,
            style: {
              'text-align': 'inherit',
              width: 'auto',
            },
            components: [
              {
                type: EMAIL_MAKER_COMPONENT_TYPES.text.PMW_TEXT,
                ...BUTTON_TABLE_PART_FLAGS,
                editable: true,
                style: {
                  margin: '0',
                  width: 'auto',
                },
                components: buttonText,
              },
            ],
          },
        ],
      },
    ],
  };
}

/** Returns the tbody child when present, otherwise the component itself. */
export function getPrimaryTableComponent(editor: Editor, component?: Component): Component | null {
  const selected = component ?? editor.getSelected();
  if (!selected) {
    return null;
  }

  const potentialTableBody = selected.components().find((child) => {
    return child.get('tagName')?.toLowerCase() === EMAIL_MAKER_COMPONENT_TYPES.default.TBODY;
  });

  if (potentialTableBody) {
    return potentialTableBody;
  }

  return selected;
}

/** Recursively collects text/background/border colors as hex. */
export function getAllColorHexesInComponent(component?: Component): string[] {
  if (!component) {
    return [];
  }

  const colors: string[] = [];
  // Reads the raw `style` prop rather than getStyle(). Boundary cast: GrapesJS types the prop loosely.
  const style = (component.get('style') ?? {}) as StyleProps;
  const values = [style.color, style['background-color'], style['border-color']];

  values.forEach((value) => {
    if (typeof value === 'string' && value) {
      colors.push(colorToHex(value));
    }
  });

  component.components().forEach((child) => {
    colors.push(...getAllColorHexesInComponent(child));
  });

  return colors;
}

export function isMultiselect(editor: Editor): boolean {
  return editor.getSelectedAll().length > 1;
}

/** Coerces a loosely-typed GrapesJS attributes/style object to a string-keyed, string-valued map. */
function coerceToStringRecord(raw: unknown): Record<string, string> {
  if (raw === null || typeof raw !== 'object') {
    return {};
  }
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === 'string') {
      result[key] = value;
    } else if (value === null || value === undefined) {
      result[key] = '';
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      result[key] = String(value);
    }
  }
  return result;
}

/** The component's attributes as a string-keyed, string-valued map, coercing each loosely-typed value at the boundary. */
export function getStringAttributes(component: Component): Record<string, string> {
  return coerceToStringRecord(component.getAttributes());
}

/** The component's computed styles as a string-keyed, string-valued map, coercing each loosely-typed value at the boundary. */
export function getStringStyles(component: Component): Record<string, string> {
  return coerceToStringRecord(component.getStyle());
}

/** Reads a component prop as a string, or `undefined` when unset / not a string. */
export function getStringProp(component: Component, key: string): string | undefined {
  const value: unknown = component.get(key);
  return typeof value === 'string' ? value : undefined;
}

/** Reads a component prop as a finite number, or `undefined` when unset / not numeric. */
export function getNumberProp(component: Component, key: string): number | undefined {
  const value: unknown = component.get(key);
  return typeof value === 'number' && !Number.isNaN(value) ? value : undefined;
}

/** Reads a component prop as a boolean flag. */
export function getBooleanProp(component: Component, key: string): boolean {
  return Boolean(component.get(key));
}

function doesComponentHaveTagName(component: Component | undefined, tagName: string): boolean {
  if (component) {
    return component.get('tagName')?.toUpperCase() === tagName;
  }

  return false;
}
